import { TWidget } from 'widgets/Widget';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { getContentByUrl } from 'widgets/toolbox/ajax';
import { TButtonInstance } from 'widgets/global/Button';
import { debounce } from 'widgets/toolbox/debounce';
import { getElementWidth } from 'widgets/toolbox/util';

/**
 * @description Base ProductTile implementation
 * @param Widget Base widget for extending
 * @returns Product Tile widget
 */
function ProductTileClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory product
     * @class ProductTile
     * @augments Widget
     * @classdesc Product Tile component. Used in different product grids.
     * @property {string} data-widget - Widget name `productTile`
     * @property {string} data-pid - product ID
     * @property {string} data-product-name - product name
     * @property {object} data-analytics - analytics data for product tile
     * @property {string} data-text-network-error - network error text, when current ajax operation ended up with network error
     * @property {string} data-text-added-to-wishlist - a text which is used to indicate, that product has being added to wishlist
     * @property {object} data-accessibility-alerts - contains accessibility alerts for product tile actions
     * @property {object} data-grid-item - indicates, that product tile is used inside product grid
     *
     * @example <caption>Product Tile widget usage</caption>
     * <section
     *     class="b-product_tile"
     *     aria-label="${product.productName}"
     *     <isif condition="${pdict.first}">
     *         data-id="firstTile"
     *     </isif>
     *     data-pid="${product.id}"
     *     data-product-name="${product.productName}"
     *     data-product-options="${JSON.stringify(product.options)}"
     *     data-product-id="${product.id}"
     *     data-widget="productTile"
     *     data-analytics="${JSON.stringify(product.gtmInfo)}"
     *     data-recommendation="true"
     *     data-text-network-error="${Resource.msg('error.alert.network', 'product', null)}"
     *     data-text-added-to-wishlist="${Resource.msg('button.added.to.wishlist', 'wishlist', null)}"
     *     data-show-alert-if-product-in-wishlist="true"
     *     data-accessibility-alerts='{
     *         "removedfromwishlist": "${Resource.msg('alert.removedfromwishlist', 'product', null)}",
     *         "addedtowishlist": "${Resource.msg('alert.addedtowishlist', 'product', null)}"
     *     }'
     *     data-grid-item=true
     *     data-tau="product_tile"
     *     data-tau-product-id="${product.id}"
     * >
     *     <isinclude template="product/productTile" />
     * </section>
     */
    class ProductTile extends Widget {
        tileWidth: number | undefined;

        leftOverCounter: number | undefined;

        swatchesExpanded: boolean | undefined;

        prefs() {
            return {
                productName: '',
                gridItem: false,
                compactClass: 'm-compact',
                orderBeforeClass: 'm-order-before',
                ...super.prefs()
            };
        }

        /**
         * @description Init widget
         * @emits "product.tile.init"
         */
        init() {
            super.init();

            this.eventBus().emit('product.tile.init', this.ref('self'));

            this.initDynamicSwatches();
        }

        onRefresh(): void {
            super.onRefresh();
            this.updateSwatches();
        }

        /**
         * @description Executes when user clicks on product details link on product tile.
         * Usually used by analytics etc.
         * @emits tile.product.link.click
         * @param link - product link
         */
        onProductLinkClick(link: TRefElementInstance) {
            const href = link.data<string>('tileModelUrl');

            if (!href) {
                this.eventBus().emit('tile.product.link.click', link);

                return Promise.resolve();
            }

            return getContentByUrl(href).then(markupFromResponse => {
                this.render(undefined, undefined, undefined, markupFromResponse).then(() => {
                    // remove "compact" state after tile re-render
                    if (this.has('swatchesContainer') && this.swatchesExpanded) {
                        this.ref('swatchesContainer').removeClass(this.prefs().compactClass);
                        const leftOverRefEl = this.ref('leftOverCounter');
                        const leftOverStr = this.leftOverCounter?.toFixed(0);

                        leftOverRefEl.setHtml('-' + leftOverStr);
                    }

                    this.eventBus().emit('tile.color.changed', this.ref('self'));
                });
            });
        }

        /**
         * @description swatches styled in compact container showed according to product tile size and current tile state
         */
        initDynamicSwatches() {
            if (this.has('swatchesContainer')) {
                this.ev('resize', debounce(this.handleResize.bind(this), 200), window);
                this.handleResize();
            }
        }

        /**
         * @description product tile resize handler
         */
        handleResize() {
            const currentWidth = this.ref('self').get()?.offsetWidth || 0;

            if (currentWidth && this.tileWidth !== currentWidth) {
                this.tileWidth = currentWidth;
                this.updateSwatches();
            }
        }

        /**
         * @description calculate how many swatches are hidden and update bubble with number if there are hidden in compact container swatches
         */
        // eslint-disable-next-line sonarjs/cognitive-complexity
        updateSwatches() {
            if (!this.has('swatchesContainer') || !this.has('swatch-1')) {
                return;
            }

            const swatchesContainer = this.ref('swatchesContainer');
            const swatchesContainerEl = swatchesContainer.get();
            const firstSwatchEl = this.ref('swatch-1').get();
            const swatchTotalCount = <number> swatchesContainer.data('swatchesCount');

            if (firstSwatchEl && swatchesContainerEl) {
                // calculate how many swatches can be displayed in one row
                const elWidth = getElementWidth(firstSwatchEl, true);
                const maxVisibleSwatchesCount = Math.floor(getElementWidth(swatchesContainerEl, false, false) / elWidth);
                const leftOverCounter = Number(swatchTotalCount || 0) - maxVisibleSwatchesCount + 1;
                const leftOverRefEl = this.ref('leftOverCounter');

                for (let swatchIndex = 1; swatchIndex <= swatchTotalCount; swatchIndex++) {
                    const refSwatchId = 'swatch-' + swatchIndex.toFixed(0);

                    if (this.has(refSwatchId)) {
                        if (swatchIndex < maxVisibleSwatchesCount) {
                            this.ref(refSwatchId).addClass(this.prefs().orderBeforeClass);
                        } else {
                            this.ref(refSwatchId).removeClass(this.prefs().orderBeforeClass);
                        }
                    }
                }

                // show and update bubble with number of hidden swatches
                if (leftOverCounter > 1 && this.has('leftOverCounter')) {
                    const leftOverStr = leftOverCounter.toFixed(0);
                    const swatchesTitle = String(leftOverRefEl.data('title')).replace('100', leftOverStr);

                    this.leftOverCounter = leftOverCounter;
                    leftOverRefEl.attr('title', swatchesTitle);
                    leftOverRefEl.attr('aria-label', swatchesTitle);
                    leftOverRefEl.setHtml('+' + leftOverStr);
                    swatchesContainer.addClass(this.prefs().compactClass);
                    leftOverRefEl.show();
                } else {
                    swatchesContainer.removeClass(this.prefs().compactClass);
                    leftOverRefEl.hide();
                }
            }
        }

        /**
         * @description show hidden swatches on left over tswatches counter click
         * @param el target element
         * @param ev click event
         */
        onLeftOverTilesLinkClick(el, ev) {
            if (this.has('swatchesContainer')) {
                ev.preventDefault();
                const leftOverRefEl = this.ref('leftOverCounter');
                const leftOverStr = this.leftOverCounter?.toFixed(0);

                this.ref('swatchesContainer').toggleClass(this.prefs().compactClass);

                const areAllSwatchesExpanded = !this.ref('swatchesContainer').hasClass(this.prefs().compactClass);

                const symbol = areAllSwatchesExpanded ? '-' : '+';

                this.swatchesExpanded = areAllSwatchesExpanded;

                leftOverRefEl.setHtml(symbol + leftOverStr);
            }
        }

        /**
         * @description On modal window show callback
         * @emits "product.tile.qv.open"
         */
        onModalShow() {
            this.eventBus().emit('product.tile.qv.open', {
                gtmInfo: this.data('analytics')
            });
        }

        showQuickView(button: TButtonInstance) {
            /**
             * @description Event dispatched, when Global Modal should be triggered
             * @event module:events#dialogshow
             */
            this.eventBus().emit('dialogshow', {
                url: button.data('url'),
                wrapperClasses: 'm-quick_view',
                ariaLabel: this.prefs().productName,
                attributes: {
                    'data-tau-unique': 'quick_view_dialog'
                }
            }, this.onModalShow.bind(this));
        }

        /**
         * @description Focus
         */
        focus() {
            this.ref('tileImageLink').focus();
        }
    }

    return ProductTile;
}

export type TProductTile = ReturnType<typeof ProductTileClassCreator>;

export type TProductTileInstance = InstanceType<TProductTile>;

export default ProductTileClassCreator;
